import { useState, useEffect } from 'react';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { SkeletonLoader, Badge, BaseLayout } from '@/components';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui';
import { Add } from '@carbon/icons-react';
import { Upload } from 'lucide-react';
import { getAccount } from '../../../api/account.api';
import type { Account } from '../../../types/accounts.types';
import { useToast } from '../../../contexts/toast-context';
import { TOAST_TYPES, BADGE_TYPES } from '../../../constants/common.constants';
import { AccountHierarchy } from '../../../components/account-hierarchy/account-hierarchy';
import { AccountDashboard } from '../../../components/dashboards/account/account-dashboard';
import { SurveyConfigTab } from '../../../components/survey-config/survey-config-tab';
import { NotificationSettingsTab } from '../../../components/notification-settings/notification-settings-tab';
import { useAuth } from '../../../hooks/use-auth';

import './view-account-style.scss';

const TABS = {
  OVERVIEW: 'overview',
  HIERARCHY: 'hierarchy',
  SURVEY_CONFIG: 'survey-config',
  NOTIFICATION_SETTINGS: 'notification-settings',
} as const;

export const Route = createFileRoute('/accounts/$uuid/')({
  component: ViewAccount,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: (search.tab as string) || TABS.OVERVIEW,
    };
  },
});

function ViewAccount() {
  const { uuid } = Route.useParams();
  const search = useSearch({ from: '/accounts/$uuid/' });
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [account, setAccount] = useState<Account | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated } = useAuth();

  const fetchAccount = async () => {
    try {
      setIsLoading(true);
      const data = await getAccount(uuid);
      setAccount(data);
    } catch (error) {
      addToast(`Failed to fetch account`, {
        type: TOAST_TYPES.ERROR,
        title: 'Fetch Error',
      });
      if (isAuthenticated) {
        navigate({ to: '/dashboard' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAccount();
  }, [uuid]);

  const handleEdit = () => {
    navigate({ to: `/accounts/${uuid}/edit` });
  };

  const handleAddUser = () => {
    navigate({
      to: '/organizations/add',
      search: { account: uuid },
    });
  };

  const handleBulkAddUsers = () => {
    navigate({
      to: '/organizations/bulk-provision',
      search: { account: uuid },
    });
  };

  const handleTabChange = (value: string) => {
    navigate({
      to: '/accounts/$uuid',
      params: { uuid },
      search: { tab: value },
    });
  };

  if (isLoading) {
    return (
      <BaseLayout
        header={{
          headerText: 'Account Details',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: ['Accounts', 'View'],
        }}
      >
        <SkeletonLoader count={10} height={30} />
      </BaseLayout>
    );
  }

  if (!account) {
    return null;
  }

  return (
    <BaseLayout
      header={{
        headerText: account.name,
        headerChildren:
          search.tab === TABS.HIERARCHY ? (
            <div className="button-container-right">
              <Button
                variant="basic-secondary"
                size="medium"
                onClick={handleBulkAddUsers}
              >
                <Upload size={16} />
                Bulk Provision
              </Button>
              <Button
                variant="basic-primary"
                size="medium"
                onClick={handleAddUser}
              >
                <Add size={16} />
                Add User
              </Button>
            </div>
          ) : undefined,
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: [`${account.name}`],
      }}
    >
      <Tabs
        value={search.tab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList>
          <TabsTrigger value={TABS.OVERVIEW}>Overview</TabsTrigger>
          <TabsTrigger value={TABS.HIERARCHY}>Hierarchy</TabsTrigger>
          <TabsTrigger value={TABS.SURVEY_CONFIG}>Survey Config</TabsTrigger>
          <TabsTrigger value={TABS.NOTIFICATION_SETTINGS}>
            Driver Notification Config
          </TabsTrigger>
        </TabsList>

        <TabsContent value={TABS.OVERVIEW}>
          <Card>
            <CardHeader className="view-account-card-header">
              <CardTitle>Account Information</CardTitle>
              <div className="button-container-right">
                <Button
                  variant="outline-secondary"
                  size="medium"
                  onClick={handleEdit}
                >
                  Edit Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="form-row">
                <div className="form-col">
                  <p className="form-label">Name</p>
                  <p>
                    {account.name}{' '}
                    <Badge
                      text={account.is_active ? 'Active' : 'Inactive'}
                      type={
                        account.is_active
                          ? BADGE_TYPES.SUCCESS
                          : BADGE_TYPES.DANGER
                      }
                    />
                  </p>
                </div>
                <div className="form-col">
                  <p className="form-label">Address</p>
                  <p>
                    {account.street_address}, {account.city}, {account.state},{' '}
                    {account.zip}
                  </p>
                </div>
                <div className="form-col"></div>
              </div>

              {/* Contact Information */}

              <div className="form-row mt-6">
                <div className="form-col">
                  <p className="form-label">Point of Contact</p>
                  <p>{account.point_of_contact}</p>
                </div>
                <div className="form-col">
                  <p className="form-label">POC Email</p>
                  <p>{account.email}</p>
                </div>

                <div className="form-col">
                  <p className="form-label">POC Phone</p>
                  <p>{account.phone}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Dashboard */}
          <AccountDashboard accountUuid={account.uuid} />
        </TabsContent>

        <TabsContent value={TABS.HIERARCHY}>
          <AccountHierarchy accountUuid={uuid} />
        </TabsContent>

        <TabsContent value={TABS.SURVEY_CONFIG}>
          <SurveyConfigTab
            accountUuid={uuid}
            hasSurveyConfig={account.has_survey_config}
            onSurveyConfigChange={(hasConfig) =>
              setAccount((prev) =>
                prev ? { ...prev, has_survey_config: hasConfig } : prev,
              )
            }
          />
        </TabsContent>

        <TabsContent value={TABS.NOTIFICATION_SETTINGS}>
          <NotificationSettingsTab accountUuid={uuid} />
        </TabsContent>
      </Tabs>
    </BaseLayout>
  );
}
